// Pure model of the hover tooltip's label (`HoverTip.tsx`). Kept apart from the component so the shape, the
// text normalisation and the staleness key can be tested without React. Relative imports only (test runner).

/** Icons a card can show; `HoverTip.tsx` maps each name to its icon. `escudo` (the UDA shield, `EscudoUda`) heads the
 *  card of an institutional line: the same mark those lines carry in the list. */
export type HoverTipIcon = 'flask' | 'building' | 'book' | 'globe' | 'tag' | 'landmark' | 'escudo';

/**
 * One value of a card row; `detail` is a secondary line shown beneath it (an OCDE subarea). `unaLinea` keeps the
 * value on ONE line (a unit name, never split in two); on a window too narrow for it, it is cut with an ellipsis.
 */
export interface HoverTipCardValue {
	text: string;
	detail?: string;
	unaLinea?: boolean;
}

/** A labelled row of a card: icon, small section label and one or more values. */
export interface HoverTipCardRow {
	icon: HoverTipIcon;
	label: string;
	values: readonly HoverTipCardValue[];
}

/**
 * A structured tooltip: a header (icon + bold title, optional dimmed caption beneath it) and labelled rows. It
 * is data only — no JSX, no icon components — so it can be built and tested anywhere.
 */
export interface HoverTipCard {
	icon: HoverTipIcon;
	title: string;
	/** The title stays on ONE line (it is a unit name); otherwise it wraps, as a line name may. */
	titleUnaLinea?: boolean;
	caption?: string;
	rows: readonly HoverTipCardRow[];
}

/**
 * What a tooltip can say. A string is one line; an array is a structured text label (first line = title, the
 * rest = detail); a card is the rich variant.
 */
export type HoverTipLabel = string | readonly string[] | HoverTipCard;

/** True for the rich variant. */
export function esTarjeta(label: HoverTipLabel | null): label is HoverTipCard {
	return typeof label === 'object' && label !== null && !Array.isArray(label) && 'title' in label && 'rows' in label;
}

/** Non-empty text lines of a text label; a card or nothing gives none. */
export function lineasDeEtiqueta(label: HoverTipLabel | null): readonly string[] {
	if (label === null || esTarjeta(label)) return [];
	return (typeof label === 'string' ? [label] : label).filter((linea) => linea.length > 0);
}

/**
 * Identity of a label by CONTENT. A structured label arrives as a NEW object on every render, so staleness
 * (the anchor now describes something else) cannot be judged by reference. A card's key starts with a prefix
 * that no text label produces, so a card is never mistaken for the text that spells the same words.
 */
export function claveDeEtiqueta(label: HoverTipLabel | null): string {
	if (esTarjeta(label)) return `\u0000card:${JSON.stringify(label)}`;
	return lineasDeEtiqueta(label).join('\n');
}

/** Box of the painted tooltip, in viewport coordinates (a `DOMRect` fits). */
export interface CajaEnVentana {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

/**
 * How far to move a painted tooltip so it stays inside the window, keeping `margen` px from each edge. A card is
 * tall and up to ~640 px wide and opens next to its anchor, so near an edge it would be cut off. Each axis is
 * independent. On a box larger than the window the top/left edge wins (the title stays readable, the overflow is
 * at the bottom/right).
 */
export function desplazamientoParaCaber(
	caja: CajaEnVentana,
	ventana: { ancho: number; alto: number },
	margen: number,
): { x: number; y: number } {
	return {
		x: desplazamientoEnEje(caja.left, caja.right, ventana.ancho, margen),
		y: desplazamientoEnEje(caja.top, caja.bottom, ventana.alto, margen),
	};
}

/** One axis: pull the far end inside the window, then make sure the near end is not pushed past its margin. */
function desplazamientoEnEje(inicio: number, fin: number, largoVentana: number, margen: number): number {
	const desplazamiento = fin > largoVentana - margen ? largoVentana - margen - fin : 0;
	return inicio + desplazamiento < margen ? margen - inicio : desplazamiento;
}
